import { ReactNode, useMemo } from 'react';
import { Box } from '@chakra-ui/react';
import { EmptyField } from 'components/elements/EmptyField';
import { ExternalLink } from 'components/elements/ExternalLink';
import { Label } from 'components/elements/Label';
import { Text } from 'components/elements/Text';
import { ModalHeader } from 'components/layout/ModalHeader';
import { ModalStatus } from 'components/layout/ModalStatus';
import MasterDocumentModal from 'components/modals/documentModals/MasterDocumentModal';
import DocumentRelatedCompanies from 'components/widgets/DocumentModals/DocumentRelatedCompanies';
import { InfoModal } from 'components/widgets/InfoModal';
import { useMunicipalHearingV4 } from 'hooks/useDocuments';
import { useGetModalActions, useGetQueryParam } from 'hooks/useModal';
import { useMunicipalHearing } from 'hooks/useMunicipalHearing';
import { trackProjectMaterials } from 'utils/analytics';
import { parseRelatedContactsIntoList } from 'utils/contacts';
import { formatProjectCost } from 'utils/currency';

const MetadataRow = ({
  label,
  children,
  hideDivider,
}: {
  label: string;
  children: ReactNode;
  hideDivider?: boolean;
}) => (
  <InfoModal.Row hideDivider={hideDivider}>
    <Label>{label}</Label>
    <InfoModal.RowDetails>{children}</InfoModal.RowDetails>
  </InfoModal.Row>
);

export const MunicipalHearing = ({
  enableFetchByDocumentId = false,
}: {
  enableFetchByDocumentId?: boolean;
}) => {
  let resolvedDocumentId: string | undefined;
  let result:
    | ReturnType<typeof useMunicipalHearingV4>
    | ReturnType<typeof useMunicipalHearing>;

  if (enableFetchByDocumentId) {
    const documentId = useGetQueryParam('documentId');
    resolvedDocumentId = documentId || undefined;
    result = useMunicipalHearingV4(documentId || undefined);
  } else {
    const documentUuid = useGetQueryParam('municipalHearing');
    resolvedDocumentId = documentUuid || undefined;
    result = useMunicipalHearing(documentUuid);
  }

  const { Skeleton, ...modalProps } = useGetModalActions(result.isLoading);

  const metadata = result.data?.metadata;
  const relatedContacts = useMemo(
    () => parseRelatedContactsIntoList(metadata?.relatedcontacts || []),
    [metadata?.relatedcontacts],
  );

  const documentUrl = metadata?.document_url ?? result.data?.document_url;

  return (
    <InfoModal
      {...modalProps}
      header={
        <ModalHeader Skeleton={Skeleton} type="Municipal Hearing">
          {metadata?.permitnum ?? ''}
        </ModalHeader>
      }
    >
      <Box>
        <MasterDocumentModal
          address={metadata?.parcel_address ?? ''}
          city={result?.data?.city ?? ''}
          state_code={result?.data?.state_code ?? ''}
          isLoading={result.isLoading}
        />

        <DocumentRelatedCompanies
          contacts={relatedContacts}
          companies={result.data?.companies}
          documentId={resolvedDocumentId}
          prefetchedAiContactSuggestionsByRelatedContactId={
            result.data?.ai_related_contact_suggestions_by_contact_id
          }
          Skeleton={Skeleton}
        />

        <MetadataRow label="Project Name">
          <Skeleton variant="text" noOfLines={1}>
            {metadata?.projectname ? (
              <Text fontWeight="bold">{metadata.projectname}</Text>
            ) : (
              <EmptyField />
            )}
          </Skeleton>
        </MetadataRow>

        <MetadataRow label="Description" hideDivider>
          <Skeleton variant="text" noOfLines={1}>
            {metadata?.description ? <Text>{metadata.description}</Text> : <EmptyField />}
          </Skeleton>
        </MetadataRow>

        <MetadataRow label="Project Value" hideDivider>
          <Skeleton variant="text" noOfLines={1}>
            {metadata?.estprojectcost != null ? (
              <Text>{formatProjectCost(metadata.estprojectcost)}</Text>
            ) : (
              <EmptyField />
            )}
          </Skeleton>
        </MetadataRow>

        <MetadataRow label="Timeline" hideDivider>
          <Skeleton variant="text" noOfLines={1}>
            {metadata?.timeline ? <Text>{metadata.timeline}</Text> : <EmptyField />}
          </Skeleton>
        </MetadataRow>

        <MetadataRow label="Public Opinion" hideDivider>
          <Skeleton variant="text" noOfLines={1}>
            {metadata?.public_opinion ? (
              <Text>{metadata.public_opinion}</Text>
            ) : (
              <EmptyField />
            )}
          </Skeleton>
        </MetadataRow>

        <MetadataRow label="Council Summary" hideDivider>
          <Skeleton variant="text" noOfLines={1}>
            {metadata?.council_summary ? (
              <Text>{metadata.council_summary}</Text>
            ) : (
              <EmptyField />
            )}
          </Skeleton>
        </MetadataRow>

        <MetadataRow label="Decision" hideDivider>
          <Skeleton variant="text" noOfLines={1}>
            {metadata?.decision ? <Text>{metadata.decision}</Text> : <EmptyField />}
          </Skeleton>
        </MetadataRow>

        <ModalStatus activities={result.data?.activities} Skeleton={Skeleton} />

        {result.isLoading && (
          <InfoModal.Row>
            <Label>Relevant Materials</Label>
            <InfoModal.RowDetails>
              <Skeleton variant="text" noOfLines={1} />
            </InfoModal.RowDetails>
          </InfoModal.Row>
        )}

        {!result.isLoading && documentUrl && (
          <InfoModal.Row>
            <Label>Relevant Materials</Label>
            <InfoModal.RowDetails>
              <ExternalLink
                to={documentUrl}
                onClick={() => trackProjectMaterials(documentUrl)}
              >
                View application
              </ExternalLink>
            </InfoModal.RowDetails>
          </InfoModal.Row>
        )}
      </Box>
    </InfoModal>
  );
};
